import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Zap, Loader2, CheckCircle, Lock, 
  Send, Eye, AlertCircle, Lightbulb, Shield, Award
} from "lucide-react";

export const RapidSession2Page = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [formData, setFormData] = useState({
    unresolved_decision: "",
    outdated_assumption: "",
    boundary_to_change: "",
    trust_decision: ""
  });

  useEffect(() => {
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      const response = await authFetch(`${API}/rapid/enrollment`, {  });
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        if (data.session_2?.pre_session_response) {
          try {
            const savedData = JSON.parse(data.session_2.pre_session_response);
            setFormData(prev => ({ ...prev, ...savedData }));
          } catch {
            // Response might be plain text from old format
          }
        }
      }
    } catch (_error) {
      console.error("Error fetching enrollment:", _error);
    } finally {
      setLoading(false);
    }
  };

  const isLocked = () => {
    if (!enrollment) return true;
    // Session 2 is unlocked after Session 1 is completed
    return !enrollment.session_1?.completed;
  };

  const isCompleted = () => {
    return enrollment?.session_2?.completed === true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await authFetch(`${API}/rapid/session/2/pre-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ response: JSON.stringify(formData) })
      });

      if (response.ok) {
        toast.success("Pre-session reflection submitted successfully!");
        fetchEnrollment();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isLocked()) {
    return (
      <div className="min-h-screen bg-neutral-50" data-testid="session-2-locked">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Lock className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-neutral-900 mb-2">Session 2 is Locked</h1>
          <p className="text-neutral-600 mb-6">Please complete Session 1 first to unlock this session.</p>
          <Button onClick={() => navigate("/rapid/session/1")} className="bg-emerald-600 hover:bg-emerald-700">
            Go to Session 1
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="rapid-session-2">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/rapid")} className="text-neutral-600">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-semibold text-neutral-900">Session 2: Plan & Implement</h1>
                {isCompleted() && <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>}
              </div>
              <p className="text-sm text-neutral-500">R.A.P.I.D.™ Business Recalibration</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Page Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
            <Zap className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Plan & Implement</h2>
          <p className="text-neutral-600 max-w-xl mx-auto">
            <strong>Objective:</strong> Recalibrate decision-making and install leadership standards aligned with your current role.
          </p>
        </div>

        {/* Section 1: Session Context */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-slate-50 border-b">
            <div className="flex items-center space-x-2">
              <Eye className="w-5 h-5 text-slate-600" />
              <CardTitle className="text-lg">What We&apos;re Doing in This Session</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-neutral-700 mb-4">
              In this session, we interrupt outdated decision logic and replace it with standards that match your current level of responsibility.
            </p>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <p className="font-medium text-blue-800">
                This is where execution begins to feel lighter and cleaner.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Pre-Session Reflection Form */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-blue-50 border-b border-blue-100">
            <CardTitle className="text-lg">Decision Recalibration Prep</CardTitle>
            <CardDescription className="text-blue-700 mt-1">
              Reflect on what you learned in Session 1.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-base font-medium">
                  1. Which decision from Session 1 still feels unresolved or inconsistent?
                </Label>
                <Textarea
                  value={formData.unresolved_decision}
                  onChange={(e) => updateField("unresolved_decision", e.target.value)}
                  placeholder="Describe the decision that's still weighing on you..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s2-unresolved"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  2. What assumption do you now recognize is no longer serving you?
                </Label>
                <Textarea
                  value={formData.outdated_assumption}
                  onChange={(e) => updateField("outdated_assumption", e.target.value)}
                  placeholder="Identify the outdated belief or assumption..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s2-assumption"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  3. What standard or boundary do you know needs to change?
                </Label>
                <Textarea
                  value={formData.boundary_to_change}
                  onChange={(e) => updateField("boundary_to_change", e.target.value)}
                  placeholder="What rule or limit needs to be updated..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s2-boundary"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  4. If you fully trusted yourself in this role, what decision would already be made?
                </Label>
                <Textarea
                  value={formData.trust_decision}
                  onChange={(e) => updateField("trust_decision", e.target.value)}
                  placeholder="What would confident you have already decided..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s2-trust"
                />
              </div>

              {!isCompleted() && (
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base"
                    disabled={submitting}
                    data-testid="s2-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit & Prepare for Session 2
                      </>
                    )}
                  </Button>
                </div>
              )}

              {enrollment?.session_2?.pre_session_response && !isCompleted() && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-800 font-medium">Pre-session reflection submitted</p>
                    <p className="text-blue-700 text-sm">Your consultant will review your responses and provide feedback during the session.</p>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Section 3: New Decision Filters (Post-Session) */}
        <Card className={`border-neutral-200 ${!isCompleted() ? "opacity-60" : ""}`}>
          <CardHeader className="bg-indigo-50 border-b border-indigo-100">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Decision Filters Installed</CardTitle>
                <CardDescription className="text-indigo-700 mt-1">
                  These filters guide future decisions and reduce overthinking.
                </CardDescription>
              </div>
              {!isCompleted() && <Lock className="w-5 h-5 text-indigo-400" />}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {isCompleted() && enrollment?.session_2?.outputs?.decision_filters ? (
              <div className="space-y-4">
                {enrollment.session_2.outputs.decision_filters.decide_by && (
                  <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
                    <p className="text-sm text-indigo-600 font-medium mb-1">&quot;At this level, I decide by…&quot;</p>
                    <p className="text-neutral-800">{enrollment.session_2.outputs.decision_filters.decide_by}</p>
                  </div>
                )}
                {enrollment.session_2.outputs.decision_filters.no_longer_delay && (
                  <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
                    <p className="text-sm text-indigo-600 font-medium mb-1">&quot;I no longer delay decisions when…&quot;</p>
                    <p className="text-neutral-800">{enrollment.session_2.outputs.decision_filters.no_longer_delay}</p>
                  </div>
                )}
                {enrollment.session_2.outputs.decision_filters.prioritize && (
                  <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
                    <p className="text-sm text-indigo-600 font-medium mb-1">&quot;This role requires me to prioritize…&quot;</p>
                    <p className="text-neutral-800">{enrollment.session_2.outputs.decision_filters.prioritize}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>Your consultant will fill this in after your session.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 4: Leadership Standards (Post-Session) */}
        <Card className={`border-neutral-200 ${!isCompleted() ? "opacity-60" : ""}`}>
          <CardHeader className="bg-purple-50 border-b border-purple-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-purple-600" />
                <div>
                  <CardTitle className="text-lg">Updated Leadership Standards</CardTitle>
                  <CardDescription className="text-purple-700 mt-1">
                    These standards define how you operate moving forward.
                  </CardDescription>
                </div>
              </div>
              {!isCompleted() && <Lock className="w-5 h-5 text-purple-400" />}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {isCompleted() && enrollment?.session_2?.outputs?.leadership_standards ? (
              <div className="space-y-6">
                {enrollment.session_2.outputs.leadership_standards.boundaries && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-purple-600" />
                      Boundaries
                    </h4>
                    <p className="text-neutral-700 bg-purple-50 p-3 rounded-lg">
                      {enrollment.session_2.outputs.leadership_standards.boundaries}
                    </p>
                  </div>
                )}
                {enrollment.session_2.outputs.leadership_standards.non_negotiables && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-purple-600" />
                      Non-negotiables
                    </h4>
                    <p className="text-neutral-700 bg-purple-50 p-3 rounded-lg">
                      {enrollment.session_2.outputs.leadership_standards.non_negotiables}
                    </p>
                  </div>
                )}
                {enrollment.session_2.outputs.leadership_standards.expectations && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-purple-600" />
                      Expectations of Self and Others
                    </h4>
                    <p className="text-neutral-700 bg-purple-50 p-3 rounded-lg">
                      {enrollment.session_2.outputs.leadership_standards.expectations}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>Your consultant will fill this in after your session.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Consultant Recommendations — visible after session complete */}
        {isCompleted() && enrollment?.session_2?.outputs?.consultant_recommendations && (
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Award className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-blue-900">Consultant Recommendations</CardTitle>
                  <CardDescription className="text-blue-700">Strategic advice from your consultant based on Session 2</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="bg-white rounded-lg p-4 border border-blue-100 whitespace-pre-wrap text-neutral-800 leading-relaxed">
                {enrollment.session_2.outputs.consultant_recommendations}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => navigate("/rapid/session/1")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Session 1
          </Button>
          {isCompleted() && (
            <Button onClick={() => navigate("/rapid/session/3")} className="bg-emerald-600 hover:bg-emerald-700">
              Continue to Session 3
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RapidSession2Page;
